import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";

// Server-side live search combobox. Loads only when opened + queries endpoint
// with ?search=<term>&limit=20 on each keystroke (200ms debounce).
export function SearchCombobox({ endpoint, placeholder, labelFn, onSelect, testId, triggerClass = "h-9", limit = 2000 }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const kw = useMemo(() => rows.map(r => (labelFn(r) || "").toLowerCase()), [rows, labelFn]);
  useEffect(() => {
    if (!open) return;
    const id = ++seq.current;
    setLoading(true);
    const t = setTimeout(() => {
      api.get(endpoint, { params: term ? { search: term, limit } : { limit } })
        .then(r => { if (id === seq.current) setRows(r.data || []); })
        .catch(() => { if (id === seq.current) setRows([]); })
        .finally(() => { if (id === seq.current) setLoading(false); });
    }, 200);
    return () => clearTimeout(t);
  }, [term, open, endpoint, limit]);
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTerm(""); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testId}
          role="combobox" aria-expanded={open} aria-haspopup="listbox" data-placeholder=""
          className={`w-full justify-between rounded-sm bg-secondary border-border ${triggerClass} text-xs font-normal px-3`}>
          <span className="text-muted-foreground truncate">{placeholder}</span>
          <ChevronDown className="w-3 h-3 opacity-50 shrink-0 ml-1"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 bg-card border-border w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={term} onValueChange={setTerm}
            placeholder="Type to search…" data-testid={`${testId}-search`} className="h-8 text-xs"/>
          <CommandList className="max-h-56">
            <CommandEmpty>{loading ? "Searching…" : "No matches."}</CommandEmpty>
            {rows.map((r, i) => (
              <CommandItem key={r.id} value={kw[i] || r.id}
                data-testid={`${testId}-opt-${r.id}`}
                onSelect={() => { onSelect(r); setOpen(false); setTerm(""); }}
                className="text-xs cursor-pointer">
                {labelFn(r)}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
